import { calculateCrosswindRaw, roundCrosswind } from './engine';
import type {
    CompareRunwaysOption,
    RunwayAnalysisBase,
    RunwayAnalysisRow,
    RunwayGhost,
    RunwayPair,
    RunwayRankingOptions,
    RunwayUsage,
    TafHourSlot,
    WindData,
    WindSourceType,
    WindSources
} from './types';

const NO_WIND: WindData = { direction: 0, speed: 0 };

const norm360 = (deg: number) => ((deg % 360) + 360) % 360;

export function angularDiff(a: number, b: number): number {
    const d = norm360(a - b);
    return Math.min(d, 360 - d);
}

export function axisDiff(a: number, b: number): number {
    const d = ((a - b) % 180 + 180) % 180;
    return Math.min(d, 180 - d);
}

export interface WindSelection {
    source: WindSourceType;
    itwsSubSource: 'runway' | 'centerfield';
    itwsFlightType: 'arrival' | 'departure';
    tafPeriodIndex: number;
    tafHourIndex: number;
    tafHourlySlots: TafHourSlot[] | null;
    userWind: WindData;
}


export interface AnalyzeRunwaysOptions {
    windSources?: WindSources;
    selection: WindSelection;
    gustFactor: number;
    magneticVariation: number;
    headingMode: 'magnetic' | 'true';
    ranking?: RunwayRankingOptions;
}

export function toTrueHeading(heading: number, magneticVariation: number, headingMode: 'magnetic' | 'true'): number {
    const h = headingMode === 'magnetic' ? heading + magneticVariation : heading;
    return ((h % 360) + 360) % 360;
}

export function resolveWindForRunway(
    runwayId: string | null | undefined,
    windSources: WindSources | undefined,
    selection: WindSelection
): WindData {
    switch (selection.source) {
        case 'metar':
            return windSources?.metar ?? NO_WIND;
        case 'datis':
            return windSources?.datis ?? NO_WIND;
        case 'itws': {
            const itws = windSources?.itws;
            if (!itws) {
                return NO_WIND;
            }
            if (selection.itwsSubSource === 'centerfield' && itws.centerfield) {
                return itws.centerfield;
            }
            const data = itws.runways?.find(r => r.runwayId === runwayId);
            if (!data) {
                return itws.centerfield ?? NO_WIND;
            }
            return (selection.itwsFlightType === 'arrival' ? data.arrival : data.departure)
                ?? data.arrival ?? data.departure ?? itws.centerfield ?? NO_WIND;
        }
        case 'taf': {
            if (selection.tafHourlySlots) {
                return selection.tafHourlySlots[selection.tafHourIndex]?.wind ?? NO_WIND;
            }
            return windSources?.taf?.periods?.[selection.tafPeriodIndex]?.wind ?? NO_WIND;
        }
        case 'user':
            return selection.userWind;
    }
}

// Shared upper bound for the table's comparison bars, so every row is drawn on one scale
// and any configured limit tick stays on-scale.
export function runwayBarScale(rows: RunwayAnalysisRow[], ranking?: RunwayRankingOptions): number {
    const observed = rows.flatMap(r => [Math.abs(r.raw.headwind), Math.abs(r.raw.crosswind)]);
    return Math.max(1, ...observed, ranking?.maxCrosswind ?? 0, ranking?.maxTailwind ?? 0);
}

export function runwayServes(usage: RunwayUsage | undefined, operation: 'arrival' | 'departure'): boolean {
    return usage == null || usage === 'both' || usage === operation;
}

export function hasRunwayUsage(rows: RunwayAnalysisBase[]): boolean {
    return rows.some(r => r.usage != null);
}

export function analyzeRunways(runways: RunwayPair[] | undefined, options: AnalyzeRunwaysOptions): RunwayAnalysisRow[] {
    if (!runways?.length) {
        return [];
    }
    const { crosswindWeight = 0, maxCrosswind, maxTailwind, forUsage, scoreRunway } = options.ranking ?? {};

    const rows = runways
        .flatMap(pair => [
            { rwy: pair.base, pair, isRecip: false },
            { rwy: pair.recip, pair, isRecip: true }
        ])
        .map(({ rwy, pair, isRecip }): RunwayAnalysisRow => {
            const heading = toTrueHeading(rwy.heading, options.magneticVariation, options.headingMode);
            const wind = resolveWindForRunway(rwy.id, options.windSources, options.selection);
            const raw = calculateCrosswindRaw(heading, wind, options.gustFactor);
            const base: RunwayAnalysisBase = {
                id: rwy.id,
                pair,
                isRecip,
                heading,
                wind,
                raw,
                result: roundCrosswind(raw),
                usage: rwy.usage
            };
            const exceeded =
                (maxCrosswind != null && Math.abs(raw.crosswind) > maxCrosswind) ||
                (maxTailwind != null && raw.headwind < -maxTailwind);
            return {
                ...base,
                score: scoreRunway ? scoreRunway(base) : raw.headwind - crosswindWeight * Math.abs(raw.crosswind),
                status: exceeded ? 'exceeded' : 'ok',
                isBest: false
            };
        });

    let best: RunwayAnalysisRow | undefined;
    for (const row of rows) {
        const eligible = row.status === 'ok' && (forUsage == null || runwayServes(row.usage, forUsage));
        if (eligible && (!best || row.score > best.score)) {
            best = row;
        }
    }
    if (best) {
        best.isBest = true;
    }
    return rows;
}

export function runwayDisplayHeading(row: RunwayAnalysisBase): number {
    return norm360(row.isRecip ? row.pair.recip.heading : row.pair.base.heading);
}

function splitRunwayId(id: string): [prefix: string, suffix: string] | null {
    let i = 0;
    while (i < id.length && id[i]! >= '0' && id[i]! <= '9') {
        i++;
    }
    return i === 0 ? null : [id.slice(0, i), id.slice(i)];
}

export function mergeRunwayLabels(ids: string[]): string {
    if (ids.length <= 1) {
        return ids[0] ?? '';
    }
    const parsed = ids.map(splitRunwayId);
    if (parsed.some(p => !p) || new Set(parsed.map(p => p![0])).size > 1) {
        return ids.join('/');
    }
    const suffixes = [...new Set(parsed.map(p => p![1]))].sort();
    return parsed[0]![0] + suffixes.join('/');
}

export function buildRunwayGhosts(
    rows: RunwayAnalysisRow[],
    compare: CompareRunwaysOption = 'none',
    alwaysInclude: string[] = []
): RunwayGhost[] {
    if (!rows.length) {
        return [];
    }
    const always = new Set(alwaysInclude);
    const wanted = Array.isArray(compare) ? new Set(compare) : null;
    const hasPair = (set: Set<string>, row: RunwayAnalysisRow) => set.has(row.pair.base.id) || set.has(row.pair.recip.id);
    const included = rows.filter(r => compare === 'all' || hasPair(always, r) || (wanted != null && hasPair(wanted, r)));
    if (!included.length) {
        return [];
    }

    const axes: { bearing: number; rows: RunwayAnalysisRow[] }[] = [];
    for (const row of included) {
        const heading = runwayDisplayHeading(row);
        const axis = axes.find(a => axisDiff(a.bearing, heading) < 1);
        if (axis) {
            axis.rows.push(row);
        } else {
            axes.push({ bearing: heading, rows: [row] });
        }
    }

    return axes.map(({ bearing, rows: axisRows }) => {
        const ends = axisRows.filter(r => angularDiff(runwayDisplayHeading(r), bearing) <= 90);
        const recipEnds = axisRows.filter(r => !ends.includes(r));
        return {
            bearing,
            label: mergeRunwayLabels(ends.map(r => r.id)),
            recipLabel: mergeRunwayLabels(recipEnds.map(r => r.id)),
            ends,
            recipEnds,
            isBest: axisRows.some(r => r.isBest)
        };
    });
}

export function pickRunwayAtBearing(ghosts: RunwayGhost[], bearing: number, toleranceDeg = 14): RunwayAnalysisRow | null {
    let picked: RunwayAnalysisRow | null = null;
    let bestDiff = Infinity;
    for (const ghost of ghosts) {
        const sides: [number, RunwayAnalysisRow[]][] = [
            [ghost.bearing + 180, ghost.ends],
            [ghost.bearing, ghost.recipEnds]
        ];
        for (const [labelBearing, candidates] of sides) {
            if (!candidates.length) {
                continue;
            }
            const diff = angularDiff(bearing, labelBearing);
            if (diff <= toleranceDeg && diff < bestDiff) {
                bestDiff = diff;
                picked = candidates[0];
            }
        }
    }
    return picked;
}

